import {
  IconCircleClose,
  StatusAutonomyActive,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentWorking,
} from "@deere/icons/new";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import "./CabControl.css";
import TickingTime from "../../../components/TickingTime";
import { hasPermission } from "../../../../permissions";
import { AppContext } from "../../../context/AppContext";
import useApiCall from "../../../hooks/useApiCall";
import { usePendingCommands } from "./tools/autonomy/PendingCommandsContext";
import {
  isPauseAutonomySuccess,
  isResumeAutonomySuccess,
} from "./tools/autonomy/autonomyPredicates";

const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

interface CabControlProps {
  currentTopicString: any;
  equipObject: any;
  cabControlMachineIcon: any;
  equip: any;
  equipmentName: any;
  equipmentModel: any;
  jobsiteAutonomyState: any;
  jobsiteAutonomyStateTimestamp: number;
  onClose?: () => void;
}

const CabControl: React.FC<CabControlProps> = ({
  currentTopicString,
  equipObject,
  cabControlMachineIcon,
  equip,
  equipmentName,
  equipmentModel,
  jobsiteAutonomyState,
  jobsiteAutonomyStateTimestamp,
  onClose,
}) => {
  const { user } = React.useContext(AppContext);
  const role = user.role;
  const isDeveloper = role === "ORCHESTRATOR_DEVELOPER";

  const { execute } = useApiCall({ errorAsToast: true });
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  const { register: registerPendingCommand, hasPending } = usePendingCommands();
  const machineId: string = equipObject?.topicPortion || "";
  const machineNameForPending = machineId.split("_")[2];
  const isCommandPending = machineNameForPending ? hasPending(machineNameForPending) : false;

  const registerPauseResumeWatcher = (commandType: "pause" | "resume") => {
    const topic = String(currentTopicString || "").replace(/\/$/, "");
    const [org, jobsite, session] = topic.split("_");
    const [machineType, machineModel, machineName] = machineId.split("_");
    const machineDisplayName = machineId.replace(/_/g, " ");
    registerPendingCommand({
      id: `${machineName}-${commandType}-${Date.now()}`,
      commandType,
      machineDisplayName,
      path: { org, jobsite, session, machineType, machineModel, machineName },
      predicate: commandType === "pause" ? isPauseAutonomySuccess : isResumeAutonomySuccess,
      onResolve: () => setLoadingButton(null),
    });
  };

  const handlePauseWithWatcher = async (buttonId: string) => {
    setLoadingButton(buttonId);
    // Silent on success; the pending-command watchdog emits the single outcome toast.
    const ok = await execute(() => sendPauseMachine());
    if (!ok) {
      setLoadingButton(null);
      return;
    }
    setShowOverrideMenu(false);
    registerPauseResumeWatcher("pause");
  };

  const handleResumeWithWatcher = async (buttonId: string) => {
    setLoadingButton(buttonId);
    // Silent on success; the pending-command watchdog emits the single outcome toast.
    const ok = await execute(() => sendResumeMachine());
    if (!ok) {
      setLoadingButton(null);
      return;
    }
    setShowOverrideMenu(false);
    registerPauseResumeWatcher("resume");
  };

  // ── Developer override menu (long-press info card) ──
  const [showOverrideMenu, setShowOverrideMenu] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const LONG_PRESS_MS = 600;

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const startLongPress = () => {
    if (!isDeveloper) return;
    longPressTriggeredRef.current = false;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setShowOverrideMenu(true);
    }, LONG_PRESS_MS);
  };

  useEffect(() => {
    return () => cancelLongPress();
  }, []);

  const executeButton = async (
    buttonId: string,
    apiFn: () => Promise<Response>,
    opts?: { successMessage?: string },
  ) => {
    setLoadingButton(buttonId);
    await execute(apiFn, opts);
    setLoadingButton(null);
    setShowOverrideMenu(false);
  };

  function handleLoaderAllowEnter() {
    return fetch(`https://` + backendURI + `/Save/LoaderMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uuid: "cue-000",
        horn_pattern: "",
        light_pattern: "",
        name: "Enter Load",
        currentTopicString: currentTopicString + "OrchestratorLoader/Cue",
      }),
    });
  }

  function handleLoaderStartLoading() {
    return fetch(`https://` + backendURI + `/Save/LoaderMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uuid: "cue-001",
        horn_pattern: "",
        light_pattern: "",
        name: "Begin Loading",
        currentTopicString: currentTopicString + "OrchestratorLoader/Cue",
      }),
    });
  }

  function handleLoaderDone() {
    return fetch(`https://` + backendURI + `/Save/LoaderMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uuid: "cue-003",
        horn_pattern: "",
        light_pattern: "",
        name: "Done Loading",
        currentTopicString: currentTopicString + "OrchestratorLoader/Cue",
      }),
    });
  }

  // ── Loader cue sender (allows specifying any uuid/name) used by the
  //    developer override menu so it can issue directional variants
  //    (cue-998l/r/b, cue-002l/r/b) in addition to the plain cues that
  //    the regular contextual buttons send (cue-000, cue-001, cue-003).
  function sendLoaderCue(uuid: string, name: string) {
    return fetch(`https://` + backendURI + `/Save/LoaderMessage`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        uuid,
        horn_pattern: "",
        light_pattern: "",
        name,
        currentTopicString: currentTopicString + "OrchestratorLoader/Cue",
      }),
    });
  }

  function sendPauseMachine() {
    return fetch("https://" + backendURI + "/Save/PauseMachine", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currentTopicString:
          currentTopicString +
          "SiteManager/" +
          "id:" +
          "userId/PAUSE/" +
          equipObject.topicPortion.replace(/\//g, "_"),
      }),
    });
  }

  function sendResumeMachine() {
    return fetch("https://" + backendURI + "/Save/ResumeMachine", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currentTopicString:
          currentTopicString +
          "SiteManager/" +
          "id:" +
          "userId/RESUME/" +
          equipObject.topicPortion.replace(/\//g, "_"),
      }),
    });
  }

  /* ── Autonomy status badge ── */
  const renderAutonomyStatus = () => {
    if (jobsiteAutonomyState === "off") {
      return (
        <div className="CabHUD-StatusBadge CabHUD-StatusBadge--off">
          <StatusAutonomyOff size={40} sx={{ marginRight: "8px" }} />
          Off
        </div>
      );
    }
    if (jobsiteAutonomyState === "ready") {
      return (
        <div className="CabHUD-StatusBadge CabHUD-StatusBadge--ready">
          <StatusAutonomyReady size={40} />
        </div>
      );
    }
    if (jobsiteAutonomyState === "paused") {
      return (
        <div className="CabHUD-StatusBadge CabHUD-StatusBadge--paused">
          <StatusAutonomyActive size={40} sx={{ marginRight: "-14px" }} />
          <StatusEquipmentIdle size={40} />
        </div>
      );
    }
    if (jobsiteAutonomyState === "working") {
      return (
        <div className="CabHUD-StatusBadge CabHUD-StatusBadge--working">
          <StatusAutonomyActive size={40} sx={{ marginRight: "-14px" }} />
          <StatusEquipmentWorking size={40} />
        </div>
      );
    }
    return null;
  };

  /* ── Derive cue-based action button ── */
  const cueUuid = equipObject?.Cue?.uuid || "";
  const isPaused = jobsiteAutonomyState === "paused";

  // Parse load direction from cue suffix: l = left, r = right, b = rear
  const loadDirection = useMemo<"left" | "right" | "rear" | null>(() => {
    if (cueUuid.endsWith("l")) return "left";
    if (cueUuid.endsWith("r")) return "right";
    if (cueUuid.endsWith("b")) return "rear";
    return null;
  }, [cueUuid]);

  const isLoadCue = cueUuid.startsWith("cue-998") || cueUuid.startsWith("cue-002");

  const loadDirectionLabel = useMemo(() => {
    if (loadDirection === "left") return "Driver\u2019s Left";
    if (loadDirection === "right") return "Driver\u2019s Right";
    if (loadDirection === "rear") return "Rear";
    return "";
  }, [loadDirection]);

  /* ── Load direction indicator with enter/exit animation ── */
  const showLoadDir = isLoadCue && !!loadDirection;
  const [loadDirMounted, setLoadDirMounted] = useState(false);
  const [loadDirAnimClass, setLoadDirAnimClass] = useState("");
  const loadDirKeyRef = useRef<string>("");
  // Track current load direction content for re-entrance on direction change
  const loadDirKey = showLoadDir ? `${loadDirection}` : "";

  useEffect(() => {
    if (showLoadDir && loadDirKey !== loadDirKeyRef.current) {
      // New direction or first appearance
      if (loadDirMounted) {
        // Direction changed — exit old, then enter new
        setLoadDirAnimClass("CabHUD-LoadDir--exit");
        const t = setTimeout(() => {
          loadDirKeyRef.current = loadDirKey;
          setLoadDirAnimClass("CabHUD-LoadDir--enter");
        }, 300);
        return () => clearTimeout(t);
      } else {
        // First appearance
        loadDirKeyRef.current = loadDirKey;
        setLoadDirMounted(true);
        setLoadDirAnimClass("CabHUD-LoadDir--enter");
      }
    } else if (!showLoadDir && loadDirMounted) {
      // Disappearing
      setLoadDirAnimClass("CabHUD-LoadDir--exit");
      const t = setTimeout(() => {
        setLoadDirMounted(false);
        setLoadDirAnimClass("");
        loadDirKeyRef.current = "";
      }, 300);
      return () => clearTimeout(t);
    }
  }, [showLoadDir, loadDirKey]);

  const renderLoadDirectionIndicator = () => {
    if (!loadDirMounted) return null;

    const isFlipped = loadDirection === "left";
    const isRear = loadDirection === "rear";

    return (
      <div className={`CabHUD-LoadDir ${loadDirAnimClass}`}>
        <div className="CabHUD-LoadDir-Visual">
          <div
            className="CabHUD-LoadDir-Icon"
            style={{ transform: isFlipped ? "scaleX(-1)" : "none" }}
          >
            {equip && (equip.icon as ReactNode)}
          </div>
          <svg
            className="CabHUD-LoadDir-Arrow"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isRear ? (
              <>
                <line
                  x1="10"
                  y1="60"
                  x2="55"
                  y2="60"
                  stroke="#068afc"
                  strokeWidth="7"
                  strokeLinecap="round"
                />
                <polyline
                  points="42,47 55,60 42,73"
                  stroke="#068afc"
                  strokeWidth="7"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            ) : (
              <>
                <line
                  x1="60"
                  y1="110"
                  x2="60"
                  y2="65"
                  stroke="#068afc"
                  strokeWidth="7"
                  strokeLinecap="round"
                />
                <polyline
                  points="47,78 60,65 73,78"
                  stroke="#068afc"
                  strokeWidth="7"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}
          </svg>
        </div>
        <div className="CabHUD-LoadDir-Label">Load {loadDirectionLabel}</div>
      </div>
    );
  };

  /* ── Action button with enter/exit animation ── */
  // Derive a stable key representing which button is currently active
  const currentTask = equipObject?.TaskDispatcher?.CurrentTask;
  const taskListTasks = equipObject?.TaskDispatcher?.TaskList?.tasks;
  const hasTaskActivity =
    (currentTask && Object.keys(currentTask).length > 0) ||
    (Array.isArray(taskListTasks) && taskListTasks.length > 0);
  const isAutonomyActive = equipObject?.VADC?.autonomy?.state?.[0] == 2;

  const activeButtonKey = useMemo(() => {
    // Hide action button when autonomy is not active or task list is empty
    if (!isAutonomyActive || !hasTaskActivity) return "";
    if (cueUuid === "cue-999") return "enter";
    if (cueUuid.startsWith("cue-998")) return "startLoading";
    if (cueUuid.startsWith("cue-002")) return "doneLoading";
    if (cueUuid === "cue-004") return isPaused ? "resume" : "pause";
    return "";
  }, [cueUuid, isPaused, hasTaskActivity, isAutonomyActive]);

  const [btnMounted, setBtnMounted] = useState(false);
  const [btnAnimClass, setBtnAnimClass] = useState("");
  const [displayedBtnKey, setDisplayedBtnKey] = useState("");
  const prevBtnKeyRef = useRef("");

  useEffect(() => {
    const newKey = activeButtonKey;
    const oldKey = prevBtnKeyRef.current;

    if (newKey && newKey !== oldKey) {
      if (btnMounted) {
        // Outgoing button — slide out, then slide in new one
        setBtnAnimClass("CabHUD-ActionBtn-wrap--exit");
        const t = setTimeout(() => {
          prevBtnKeyRef.current = newKey;
          setDisplayedBtnKey(newKey);
          setBtnAnimClass("CabHUD-ActionBtn-wrap--enter");
        }, 280);
        return () => clearTimeout(t);
      } else {
        // First appearance
        prevBtnKeyRef.current = newKey;
        setDisplayedBtnKey(newKey);
        setBtnMounted(true);
        setBtnAnimClass("CabHUD-ActionBtn-wrap--enter");
      }
    } else if (!newKey && btnMounted) {
      // Button disappearing
      setBtnAnimClass("CabHUD-ActionBtn-wrap--exit");
      const t = setTimeout(() => {
        setBtnMounted(false);
        setBtnAnimClass("");
        setDisplayedBtnKey("");
        prevBtnKeyRef.current = "";
      }, 280);
      return () => clearTimeout(t);
    }
  }, [activeButtonKey]);

  const renderActionButton = () => {
    if (!btnMounted) return null;

    let buttonContent: React.ReactNode = null;

    if (displayedBtnKey === "enter") {
      buttonContent = (
        <button
          className="CabHUD-ActionBtn CabHUD-ActionBtn--enter"
          onClick={() =>
            executeButton("enterZone", () => handleLoaderAllowEnter(), {
              successMessage: "Enter zone command sent",
            })
          }
          disabled={loadingButton !== null}
        >
          {loadingButton === "enterZone" ? (
            <span className="CabHUD-ActionBtn-loading">Sending…</span>
          ) : (
            "Enter Zone"
          )}
        </button>
      );
    } else if (displayedBtnKey === "startLoading") {
      buttonContent = (
        <button
          className="CabHUD-ActionBtn CabHUD-ActionBtn--startLoading"
          onClick={() =>
            executeButton("startLoading", () => handleLoaderStartLoading(), {
              successMessage: "Start loading command sent",
            })
          }
          disabled={loadingButton !== null}
        >
          {loadingButton === "startLoading" ? (
            <span className="CabHUD-ActionBtn-loading">Sending…</span>
          ) : (
            "Start Loading"
          )}
        </button>
      );
    } else if (displayedBtnKey === "doneLoading") {
      buttonContent = (
        <button
          className="CabHUD-ActionBtn CabHUD-ActionBtn--doneLoading"
          onClick={() =>
            executeButton("doneLoading", () => handleLoaderDone(), {
              successMessage: "Done loading command sent",
            })
          }
          disabled={loadingButton !== null}
        >
          {loadingButton === "doneLoading" ? (
            <span className="CabHUD-ActionBtn-loading">Sending…</span>
          ) : (
            "Done Loading"
          )}
        </button>
      );
    } else if (displayedBtnKey === "resume") {
      buttonContent = (
        <button
          className="CabHUD-ActionBtn CabHUD-ActionBtn--resume"
          onClick={() => handleResumeWithWatcher("resume")}
          disabled={loadingButton !== null || isCommandPending}
        >
          {loadingButton === "resume" ? (
            <span className="CabHUD-ActionBtn-loading">Sending…</span>
          ) : (
            "Resume"
          )}
        </button>
      );
    } else if (displayedBtnKey === "pause") {
      buttonContent = (
        <button
          className="CabHUD-ActionBtn CabHUD-ActionBtn--pause"
          onClick={() => handlePauseWithWatcher("pause")}
          disabled={loadingButton !== null || isCommandPending}
        >
          {loadingButton === "pause" ? (
            <span className="CabHUD-ActionBtn-loading">Sending…</span>
          ) : (
            "Pause"
          )}
        </button>
      );
    }

    return <div className={`CabHUD-ActionBtn-wrap ${btnAnimClass}`}>{buttonContent}</div>;
  };

  return (
    <div className="CabHUD">
      {/* ── TOP-RIGHT: Close button ── */}
      {onClose && (
        <div className="CabHUD-Close" onClick={onClose}>
          <IconCircleClose size={40} />
        </div>
      )}

      {/* ── TOP-LEFT: Info card + load direction dropdown ── */}
      <div className="CabHUD-InfoGroup">
        <div
          className={`CabHUD-InfoCard ${loadDirMounted ? "CabHUD-InfoCard--withDropdown" : ""} ${isDeveloper ? "CabHUD-InfoCard--devPressable" : ""}`}
          onMouseDown={startLongPress}
          onMouseUp={cancelLongPress}
          onMouseLeave={cancelLongPress}
          onTouchStart={startLongPress}
          onTouchEnd={cancelLongPress}
          onTouchCancel={cancelLongPress}
          onContextMenu={(e) => {
            // Suppress browser context menu when long-press is used on touch devices
            if (longPressTriggeredRef.current) e.preventDefault();
          }}
        >
          <div className="CabHUD-InfoCard-Icon">{equip && (equip.icon as ReactNode)}</div>
          <div className="CabHUD-InfoCard-Details">
            <div className="CabHUD-MachineName">{equipmentName}</div>
            <div className="CabHUD-MachineModel">{equipmentModel}</div>
            <div className="CabHUD-StatusRow">
              {renderAutonomyStatus()}
              <div
                className={`CabHUD-Timer${jobsiteAutonomyState ? ` CabHUD-Timer--${jobsiteAutonomyState}` : ""}`}
              >
                <TickingTime timestamp={jobsiteAutonomyStateTimestamp} />
              </div>
            </div>
          </div>
        </div>
        {renderLoadDirectionIndicator()}
      </div>

      {/* ── BOTTOM-LEFT: Primary action button (cycles by cue) ── */}
      <div className="CabHUD-BottomLeft">{renderActionButton()}</div>

      {/* ── Developer override menu (long-press on info card) ── */}
      {isDeveloper && showOverrideMenu && (
        <div className="CabHUD-OverrideBackdrop" onClick={() => setShowOverrideMenu(false)}>
          <div className="CabHUD-OverridePanel" onClick={(e) => e.stopPropagation()}>
            <div className="CabHUD-OverrideHeader">
              <div className="CabHUD-OverrideTitle">Developer Override</div>
              <div className="CabHUD-OverrideSubtitle">
                Send any cab-control action regardless of current state
              </div>
            </div>
            <div className="CabHUD-OverrideList">
              <div className="CabHUD-OverrideSection">Operator Actions</div>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--enter"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton("override-enter", () => handleLoaderAllowEnter(), {
                    successMessage: "Enter zone command sent",
                  })
                }
              >
                Enter Zone
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--startLoading"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton("override-startLoading", () => handleLoaderStartLoading(), {
                    successMessage: "Start loading command sent",
                  })
                }
              >
                Start Loading
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--doneLoading"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton("override-doneLoading", () => handleLoaderDone(), {
                    successMessage: "Done loading command sent",
                  })
                }
              >
                Done Loading
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--pause"
                disabled={loadingButton !== null || isCommandPending}
                onClick={() => handlePauseWithWatcher("override-pause")}
              >
                Pause
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--resume"
                disabled={loadingButton !== null || isCommandPending}
                onClick={() => handleResumeWithWatcher("override-resume")}
              >
                Resume
              </button>

              <div className="CabHUD-OverrideSection">Machine State Cues (Directional)</div>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-atWait",
                    () => sendLoaderCue("cue-999", "At Wait"),
                    {
                      successMessage: "Cue cue-999 (At Wait) sent",
                    },
                  )
                }
              >
                At Wait <span className="CabHUD-OverrideBtn-cue">cue-999</span>
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-998l",
                    () => sendLoaderCue("cue-998l", "At Load Point"),
                    {
                      successMessage: "Cue cue-998l (At Load Point — Left) sent",
                    },
                  )
                }
              >
                At Load Point — Driver&rsquo;s Left
                <span className="CabHUD-OverrideBtn-cue">cue-998l</span>
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-998r",
                    () => sendLoaderCue("cue-998r", "At Load Point"),
                    {
                      successMessage: "Cue cue-998r (At Load Point — Right) sent",
                    },
                  )
                }
              >
                At Load Point — Driver&rsquo;s Right
                <span className="CabHUD-OverrideBtn-cue">cue-998r</span>
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-998b",
                    () => sendLoaderCue("cue-998b", "At Load Point"),
                    {
                      successMessage: "Cue cue-998b (At Load Point — Rear) sent",
                    },
                  )
                }
              >
                At Load Point — Rear
                <span className="CabHUD-OverrideBtn-cue">cue-998b</span>
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-002l",
                    () => sendLoaderCue("cue-002l", "Waiting for loading to finish"),
                    {
                      successMessage: "Cue cue-002l (Waiting for Done — Left) sent",
                    },
                  )
                }
              >
                Waiting for Done — Driver&rsquo;s Left
                <span className="CabHUD-OverrideBtn-cue">cue-002l</span>
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-002r",
                    () => sendLoaderCue("cue-002r", "Waiting for loading to finish"),
                    {
                      successMessage: "Cue cue-002r (Waiting for Done — Right) sent",
                    },
                  )
                }
              >
                Waiting for Done — Driver&rsquo;s Right
                <span className="CabHUD-OverrideBtn-cue">cue-002r</span>
              </button>
              <button
                className="CabHUD-OverrideBtn CabHUD-OverrideBtn--state"
                disabled={loadingButton !== null}
                onClick={() =>
                  executeButton(
                    "override-state-002b",
                    () => sendLoaderCue("cue-002b", "Waiting for loading to finish"),
                    {
                      successMessage: "Cue cue-002b (Waiting for Done — Rear) sent",
                    },
                  )
                }
              >
                Waiting for Done — Rear
                <span className="CabHUD-OverrideBtn-cue">cue-002b</span>
              </button>
            </div>
            <button className="CabHUD-OverrideCancel" onClick={() => setShowOverrideMenu(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CabControl;
